import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Utensils, MapPin, Scale, Coffee, Clock, Check, Plus, Minus, Calculator } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { COLORS, IMAGES, fadeInUp, staggerContainer, scaleIn } from '../config';

const stepIcons = [
  <MapPin key="1" size={32} />,
  <Utensils key="2" size={32} />,
  <Scale key="3" size={32} />,
  <Coffee key="4" size={32} />,
];
const stepImages = [IMAGES.stepInterior, IMAGES.dessert, IMAGES.stepWeighing, IMAGES.buffet];

const featureIcons = [
  <Clock key="0" size={28} />,
  <Scale key="1" size={28} />,
  <Utensils key="2" size={28} />,
  <Check key="3" size={28} />,
  <Coffee key="4" size={28} />,
  <MapPin key="5" size={28} />,
];

/* ── Price Simulator ── */
const PriceSimulator = ({ pData, language }) => {
  const sim = pData?.[`simulator_${language}`];
  const pricePerHecto = pData?.prezzo_per_etto || 2.2;
  const coverCharge = pData?.coperto ?? 0;
  const waterCharge = pData?.acqua ?? 0;

  const [plates, setPlates] = useState([]);

  if (!sim) return null;

  const addItem = (item) => {
    setPlates((prev) => [...prev, { name: item.name, weight: item.default_weight, fixed_price: item.fixed_price }]);
  };
  const removeItem = (idx) => {
    setPlates((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateWeight = (idx, w) => {
    setPlates((prev) => prev.map((p, i) => (i === idx ? { ...p, weight: Math.max(50, Math.min(500, w)) } : p)));
  };

  const plateCost = (p) => (p.fixed_price != null ? p.fixed_price : (p.weight / 100) * pricePerHecto);
  const totalWeight = plates.reduce((s, p) => s + (p.fixed_price != null ? 0 : p.weight), 0);
  const foodCost = plates.reduce((s, p) => s + plateCost(p), 0);
  const grandTotal = plates.length > 0 ? foodCost + coverCharge + waterCharge : 0;
  const fmt = (n) => language === 'it' ? n.toFixed(2).replace('.', ',') + '\u20AC' : '\u20AC' + n.toFixed(2);

  return (
    <section className="py-24" style={{ backgroundColor: COLORS.vaniglia }} data-testid="price-simulator">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
          <motion.div variants={fadeInUp} className="mb-4" style={{ color: COLORS.gold }}>
            <Calculator size={40} className="mx-auto" />
          </motion.div>
          <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-4xl md:text-5xl mb-3" style={{ color: COLORS.redwine }}>
            {sim.title}
          </motion.h2>
          <motion.p variants={fadeInUp} style={{ color: `${COLORS.nero}99` }}>{sim.subtitle}</motion.p>
        </motion.div>

        <motion.div variants={scaleIn} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="rounded-2xl overflow-hidden shadow-xl" style={{ backgroundColor: 'white' }}>

          {/* Add buttons */}
          <div className="p-6 grid grid-cols-2 sm:grid-cols-4 gap-3" style={{ borderBottom: `1px solid ${COLORS.redwine}15` }}>
            {sim.items.map((item, i) => (
              <button key={i} onClick={() => addItem(item)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all hover:scale-105 active:scale-95"
                style={{ backgroundColor: `${COLORS.redwine}10`, color: COLORS.redwine }}
                data-testid={`sim-add-${i}`}
              >
                <Plus size={16} /> {item.name}
              </button>
            ))}
          </div>

          {/* Plate items */}
          <div className="p-6 min-h-[120px]">
            <p className="text-xs tracking-wider uppercase font-semibold mb-4" style={{ color: COLORS.gold }}>{sim.your_plate}</p>
            {plates.length === 0 ? (
              <p className="text-center py-6 italic" style={{ color: `${COLORS.nero}66` }}>{sim.empty}</p>
            ) : (
              <div className="space-y-3">
                {plates.map((plate, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: `${COLORS.vaniglia}` }}>
                    <span className="font-medium flex-1 text-sm" style={{ color: COLORS.nero }}>{plate.name}</span>
                    {plate.fixed_price != null ? (
                      <span className="px-3 text-center text-xs italic" style={{ color: `${COLORS.nero}99` }}>
                        {language === 'it' ? 'Porzione fissa' : 'Fixed portion'}
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateWeight(idx, plate.weight - 25)}
                          className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-110"
                          style={{ backgroundColor: `${COLORS.redwine}15`, color: COLORS.redwine }}>
                          <Minus size={14} />
                        </button>
                        <span className="w-16 text-center font-semibold text-sm" style={{ color: COLORS.redwine }}>
                          {plate.weight}{sim.grams}
                        </span>
                        <button onClick={() => updateWeight(idx, plate.weight + 25)}
                          className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-110"
                          style={{ backgroundColor: `${COLORS.redwine}15`, color: COLORS.redwine }}>
                          <Plus size={14} />
                        </button>
                      </div>
                    )}
                    <span className="w-16 text-right font-semibold text-sm" style={{ color: COLORS.gold }}>
                      {fmt(plateCost(plate))}
                    </span>
                    <button onClick={() => removeItem(idx)}
                      className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-110"
                      style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>
                      <Minus size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Totals */}
          {plates.length > 0 && (
            <div className="p-6" style={{ backgroundColor: COLORS.redwine }}>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between" style={{ color: `${COLORS.vaniglia}cc` }}>
                  <span>{sim.food_total} ({totalWeight}{sim.grams})</span><span>{fmt(foodCost)}</span>
                </div>
                <div className="flex justify-between" style={{ color: `${COLORS.vaniglia}cc` }}>
                  <span>{sim.cover}</span><span>{fmt(coverCharge)}</span>
                </div>
                <div className="flex justify-between" style={{ color: `${COLORS.vaniglia}cc` }}>
                  <span>{sim.water}</span><span>{fmt(waterCharge)}</span>
                </div>
                <div className="my-2" style={{ borderTop: `1px solid ${COLORS.vaniglia}33` }} />
                <div className="flex justify-between text-lg font-bold" style={{ color: COLORS.vaniglia }}>
                  <span>{sim.grand_total}</span>
                  <span style={{ color: COLORS.gold }} data-testid="sim-total">{fmt(grandTotal)}</span>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

/* ── Home Page ── */
const Home = () => {
  const { language, t } = useLanguage();
  const quickInfo = t('home.quickInfo');
  const steps = t('howItWorks.steps');
  const features = t('comeFunzionaPage.features');

  const [pData, setPData] = useState(null);

  useEffect(() => {
    fetch('/prezzi.json').then(r => r.json()).then(setPData).catch(() => {});
  }, []);

  const p = pData?.[`pricing_${language}`];

  return (
    <>
      {/* ═══════════ HERO ═══════════ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(114,47,55,0.92) 0%, rgba(30,58,95,0.85) 100%)' }} />
        <div className="absolute inset-0 opacity-5"><div className="w-full h-full bg-pattern" /></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="text-center">
            <motion.div variants={fadeInUp} className="mb-8">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4A017]/40 text-[#D4A017] text-xs tracking-[0.2em] font-medium">
                <Utensils size={14} />{t('hero.badge')}
              </span>
            </motion.div>
            <motion.h1 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal text-[#F5EDE0] leading-tight mb-4">{t('hero.title')}</motion.h1>
            <motion.p variants={fadeInUp} className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl italic text-[#D4A017] mb-8">{t('hero.subtitle')}</motion.p>
            <motion.p variants={fadeInUp} className="max-w-2xl mx-auto text-[#F5EDE0]/80 text-base md:text-lg leading-relaxed mb-12">{t('hero.description')}</motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-8 md:gap-16">
              {[
                { value: language === 'it' ? '2,20\u20AC' : '2.20\u20AC', label: t('stats.pricePerHecto') },
                { value: '20+', label: t('stats.freshDishes') },
                { value: '100%', label: t('stats.italianRecipes') },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-semibold text-[#F5EDE0]">{stat.value}</p>
                  <p className="text-[#F5EDE0]/60 text-xs tracking-wider uppercase mt-1">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
            <a href="#come-funziona" className="flex flex-col items-center gap-2 text-[#F5EDE0]/60 hover:text-[#F5EDE0] transition-colors">
              <span className="text-sm">{t('hero.scrollText')}</span>
              <ChevronDown className="scroll-indicator" size={20} />
            </a>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 100V60C240 20 480 0 720 20C960 40 1200 80 1440 60V100H0Z" fill="#F5EDE0" />
          </svg>
        </div>
      </section>

      {/* ═══════════ COME FUNZIONA - Steps ═══════════ */}
      <section id="come-funziona" className="py-24" style={{ backgroundColor: COLORS.vaniglia }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <motion.p variants={fadeInUp} className="text-xs tracking-[0.2em] font-semibold uppercase mb-4" style={{ color: COLORS.redwine }}>{t('howItWorks.overline')}</motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-4xl md:text-5xl mb-4" style={{ color: COLORS.redwine }} data-testid="come-funziona-title">{t('howItWorks.title')}</motion.h2>
            <motion.div variants={fadeInUp} className="w-16 h-1 mx-auto" style={{ backgroundColor: COLORS.gold }} />
          </motion.div>
          <div className="space-y-24">
            {Array.isArray(steps) && steps.map((step, index) => (
              <motion.div key={index} variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <motion.div variants={fadeInUp} className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="relative rounded-xl overflow-hidden shadow-xl">
                    <img src={stepImages[index]} alt={step.title} className="block w-full h-80 object-cover" />
                    <div className="absolute top-4 left-4 w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold shadow-lg z-10" style={{ backgroundColor: COLORS.gold, color: COLORS.redwine }}>{index + 1}</div>
                  </div>
                </motion.div>
                <motion.div variants={fadeInUp} className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="mb-6" style={{ color: COLORS.redwine }}>{stepIcons[index]}</div>
                  <h3 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl mb-2 italic" style={{ color: COLORS.redwine }}>{step.title}</h3>
                  <p className="text-sm tracking-wider uppercase font-medium mb-4" style={{ color: COLORS.gold }}>{step.subtitle}</p>
                  <p className="text-lg leading-relaxed" style={{ color: `${COLORS.nero}b3` }}>{step.description}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ IL PREZZO - Main (from JSON) ═══════════ */}
      {p && (
        <section id="il-prezzo" className="py-24" style={{ backgroundColor: COLORS.vaniglia }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <motion.p variants={fadeInUp} className="text-xs tracking-[0.2em] font-semibold uppercase mb-4" style={{ color: COLORS.redwine }}>{p.overline}</motion.p>
                <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-4xl md:text-5xl mb-2" style={{ color: COLORS.redwine }} data-testid="prezzo-title">
                  {p.title}<br /><span className="italic" style={{ color: `${COLORS.redwine}cc` }}>{p.titleItalic}</span>
                </motion.h2>
                <motion.p variants={fadeInUp} className="leading-relaxed mt-6 mb-8" style={{ color: `${COLORS.nero}b3` }}>{p.description}</motion.p>
                <motion.div variants={fadeInUp} className="space-y-4">
                  {p.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: COLORS.redwine }}><Check size={12} style={{ color: COLORS.vaniglia }} /></div>
                      <div><p className="font-semibold" style={{ color: COLORS.redwine }}>{f.title}</p><p className="text-sm" style={{ color: `${COLORS.nero}99` }}>{f.desc}</p></div>
                    </div>
                  ))}
                </motion.div>
              </motion.div>
              <motion.div variants={scaleIn} initial="hidden" whileInView="visible" viewport={{ once: true }} className="flex justify-center">
                <div
                  className="relative p-10 max-w-sm w-full rounded-2xl flex flex-col items-center justify-center min-h-[440px] overflow-hidden"
                  style={{ background: `linear-gradient(145deg, ${COLORS.redwine} 0%, ${COLORS.navy} 100%)`, border: `3px solid ${COLORS.gold}` }}
                  data-testid="pricing-card"
                >
                  {/* Decorative icon */}
                  <motion.div
                    initial={{ scale: 0, rotate: -30 }}
                    whileInView={{ scale: 1, rotate: 0 }}
                    viewport={{ once: true }}
                    transition={{ type: 'spring', stiffness: 160, damping: 14, delay: 0.2 }}
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${COLORS.gold}20`, border: `1px solid ${COLORS.gold}66`, color: COLORS.gold }}
                  >
                    <Scale size={30} />
                  </motion.div>

                  <p className="text-xs tracking-[0.25em] uppercase text-center mb-5" style={{ color: `${COLORS.vaniglia}99` }}>
                    {p.card_label}
                  </p>

                  <div className="text-center mb-6">
                    <span className="font-['Cormorant_Garamond'] text-7xl md:text-8xl font-semibold leading-none" style={{ color: COLORS.gold }}>{language === 'it' ? '2,20' : '2.20'}</span>
                    <span className="text-3xl align-top ml-1" style={{ color: COLORS.gold }}>{'\u20AC'}</span>
                    <div className="mt-3">
                      <span className="inline-block px-4 py-1 rounded-full text-sm" style={{ backgroundColor: `${COLORS.vaniglia}15`, color: COLORS.vaniglia }}>
                        {p.card_unit}
                      </span>
                    </div>
                  </div>

                  <div className="w-20 h-px mb-6" style={{ backgroundColor: `${COLORS.vaniglia}33` }} />

                  <p
                    className="font-['Cormorant_Garamond'] italic text-center text-lg md:text-xl leading-snug px-2"
                    style={{ color: `${COLORS.vaniglia}cc` }}
                  >
                    &ldquo;{p.card_tagline}&rdquo;
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* ═══════════ CALCOLA IL TUO PASTO (replaces old breakdown) ═══════════ */}
      <PriceSimulator pData={pData} language={language} />

      {/* ═══════════ IL PREZZO - Examples (from JSON) ═══════════ */}
      {p && (
        <section className="py-24" style={{ backgroundColor: COLORS.vaniglia }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
              <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-4xl md:text-5xl mb-4" style={{ color: COLORS.redwine }}>{p.examples_title}</motion.h2>
              <motion.div variants={fadeInUp} className="w-16 h-1 mx-auto" style={{ backgroundColor: COLORS.gold }} />
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {p.examples_meals.map((meal, i) => {
                const imgs = [
                  'https://customer-assets.emergentagent.com/job_web-import/artifacts/fblskcg9_image.png',
                  'https://customer-assets.emergentagent.com/job_web-import/artifacts/35n1zhq1_image.png',
                  'https://customer-assets.emergentagent.com/job_web-import/artifacts/3aczckz0_image.png',
                ];
                const pricePerH = pData?.prezzo_per_etto || 2.2;
                return (
                  <motion.div key={i} variants={scaleIn} initial="hidden" whileInView="visible" viewport={{ once: true }} className="rounded-xl overflow-hidden shadow-lg" style={{ backgroundColor: 'white' }}>
                    <img src={imgs[i]} alt={meal.title} className="block w-full h-64 object-cover" />
                    <div className="p-6">
                      <h3 className="font-['Cormorant_Garamond'] text-xl mb-4" style={{ color: COLORS.redwine }}>{meal.title}</h3>
                      <div className="space-y-2 text-sm mb-4">
                        {meal.items.map((item, j) => {
                          const fixed = meal.fixed_prices && meal.fixed_prices[j] != null ? meal.fixed_prices[j] : null;
                          const cost = fixed != null ? fixed : (meal.weights[j] / 100) * pricePerH;
                          const display = language === 'it' ? cost.toFixed(2).replace('.', ',') + '\u20AC' : '\u20AC' + cost.toFixed(2);
                          return <div key={j} className="flex justify-between" style={{ color: COLORS.nero }}><span>{item}</span><span>{display}</span></div>;
                        })}
                      </div>
                      <div className="pt-4 flex justify-between font-semibold" style={{ borderTop: `1px solid ${COLORS.redwine}20`, color: COLORS.redwine }}>
                        <span>{p.examples_total}</span><span style={{ color: COLORS.gold }}>{meal.total}</span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════ PERCHÉ SCEGLIERE LOLA (moved to end) ═══════════ */}
      <section className="py-24" style={{ backgroundColor: COLORS.navy }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-4xl md:text-5xl mb-4" style={{ color: COLORS.vaniglia }}>{t('comeFunzionaPage.whyChoose.title')}</motion.h2>
            <motion.div variants={fadeInUp} className="w-16 h-1 mx-auto" style={{ backgroundColor: COLORS.sage }} />
          </motion.div>
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.isArray(features) && features.map((feature, index) => (
              <motion.div key={index} variants={scaleIn}
                className="p-6 rounded-xl group cursor-default transition-all duration-300 hover:scale-[1.03] hover:shadow-lg"
                style={{ backgroundColor: `${COLORS.vaniglia}10`, border: `1px solid ${COLORS.vaniglia}15` }}
              >
                <div className="mb-4 transition-all duration-300 group-hover:scale-125 group-hover:rotate-6" style={{ color: COLORS.sage }}>
                  {featureIcons[index]}
                </div>
                <h3 className="font-semibold text-lg mb-2 transition-colors duration-300 group-hover:text-[#D4A017]" style={{ color: COLORS.vaniglia }}>{feature.title}</h3>
                <p className="text-sm" style={{ color: `${COLORS.vaniglia}99` }}>{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════ QUICK INFO ═══════════ */}
      <section className="py-16" style={{ backgroundColor: COLORS.redwine }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: <MapPin size={32} /> },
              { icon: <Coffee size={32} /> },
              { icon: <Scale size={32} /> },
              { icon: <Utensils size={32} /> },
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center gap-3 group cursor-default">
                <span className="transition-all duration-300 group-hover:scale-125 group-hover:-translate-y-1" style={{ color: COLORS.gold }}>{item.icon}</span>
                <p style={{ color: COLORS.vaniglia }} className="text-sm">{Array.isArray(quickInfo) ? quickInfo[index] : ''}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
